// Dependencies
import DOMPurify from "dompurify";
import linkifyHtml from "linkify-html";
import { marked } from "marked";
import markedLinkifyIt from "marked-linkify-it";

// Styles
import css from "./markdown.module.scss";

/**
 * Setup the global configuration for the markdown parser
 */
const renderer = new marked.Renderer();

// Disable images
renderer.image = () => {
	return "";
};

// Disable checkboxes (task lists)
renderer.checkbox = () => {
	return "";
};

// Disable bold
renderer.strong = (text) => {
	return text;
};

// Disable italics
renderer.em = (text) => {
	return text;
};

// Disable headings
renderer.heading = (text, level) => {
	return `<p>${"#".repeat(level)} ${text}</p>`;
};

// Disable ordered list
renderer.list = (text) => {
	return `<p>${text}</p>`;
};

// Disable list items
renderer.listitem = (text) => {
	return `<p>${text}</p>`;
};

// Wrapper around codeblock for styling purposes
renderer.code = (code) => {
	return `<div class="${css.codeblock}"><pre><code class="${css.codeblock}">${code}</code></pre></div>`;
};

// Wrapper around table for styling purposes
renderer.table = (header, body) => {
	// const tableHtml = defaultTableRenderer(header, body);
	const tableHtml = marked.Renderer.prototype.table.call(renderer, header, body);

	return `<div class="${css.table}">${tableHtml}</div>`;
};

marked.use(markedLinkifyIt({}, {}));

marked.setOptions({
	renderer,
	gfm: true,
	breaks: true,
	sanitize: false,
	smartLists: true,
	smartypants: true,
});

/**
 * Converts markdown to html and sanitizes the output.
 */
export const markdownToHtml = (text: string) => {
	// in order to get blockquotes to work, we need to replace the &gt; with >
	let markdown = text;
	markdown = markdown.replace(/&gt;/g, ">");

	// generate the markdown html
	// const html = marked(markdown), for now

	// generate only the links
	const html = linkifyHtml(markdown, {
		attributes: {
			target: "_blank",
			rel: "noopener noreferrer",
		},
	});

	DOMPurify.addHook("afterSanitizeAttributes", (node) => {
		// set all elements owning target to target=_blank
		if ("target" in node) {
			node.setAttribute("target", "_blank");
			node.setAttribute("rel", "noopener noreferrer");
		}
	});

	// sanitize the output
	const sanitizedHtml = DOMPurify.sanitize(html);

	return sanitizedHtml;
};

/**
 * Converts markdown to html and adds it to the dom. This function will
 * only convert elements given to it as a parameter. It will not convert
 * any elements that are added to the dom after this function is called.
 * @param targetClass - A collection of elements to convert to markdown
 */
export const markdownify = (htmlElements: HTMLCollectionOf<Element>, addClassList = true) => {
	for (let i = 0; i < htmlElements.length; i++) {
		const element = htmlElements[i];

		// add the markdown class to the element, this will style the content
		addClassList && element.classList.add(css.markdown);

		// in order to get blockquotes to work, we need to replace the &gt; with >
		const markdown = element.innerHTML;

		// translate the markdown to html
		const html = markdownToHtml(markdown);

		// replace the original content with the new markdown content
		element.innerHTML = html;
	}
};

(window as any).markdownify = markdownify;
